import React, { useEffect, useState } from 'react';
import { Row, Col, Card, Spinner, Form, Button, Alert, Badge } from 'react-bootstrap';
import { FaTrash } from 'react-icons/fa';
import ApiService from '../../services/ApiService';
import { PRECIO_CONFIG } from '../../constants';
import './Admin.css';

const ESTADOS_DASHBOARD = [
  { id: 'CREADO', label: 'Creado', variant: 'warning' },
  { id: 'COBRADO', label: 'Cobrado', variant: 'cobrado' },
  { id: 'ENTREGADO', label: 'Entregado', variant: 'success' },
  { id: 'CANCELADO', label: 'Cancelado', variant: 'danger' },
  { id: 'RECHAZADO', label: 'Rechazado', variant: 'rechazado' },
];

const POR_ESTADO_VACIO = {
  CREADO: 0,
  COBRADO: 0,
  ENTREGADO: 0,
  CANCELADO: 0,
  RECHAZADO: 0,
};

function formatMoney(value) {
  return `$${Number(value || 0).toLocaleString()}`;
}

function Admin() {
  const [totalVentas, setTotalVentas] = useState(0);
  const [cantidadPedidos, setCantidadPedidos] = useState(0);
  const [cantidadPedidosSinCosto, setCantidadPedidosSinCosto] = useState(0);
  const [totalCostoProductos, setTotalCostoProductos] = useState(0);
  const [totalCostoEnvio, setTotalCostoEnvio] = useState(0);
  const [totalGanancia, setTotalGanancia] = useState(0);
  const [porEstado, setPorEstado] = useState(POR_ESTADO_VACIO);
  const [topProductos, setTopProductos] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [precioMinorista, setPrecioMinorista] = useState('7000');
  const [precioMayorista, setPrecioMayorista] = useState('12000');
  const [loadingEnvios, setLoadingEnvios] = useState(false);
  const [savingEnvios, setSavingEnvios] = useState(false);
  const [enviosError, setEnviosError] = useState(null);
  const [enviosOk, setEnviosOk] = useState(null);

  // Establecer fechas por defecto: mes actual
  useEffect(() => {
    const today = new Date();
    const firstDay = new Date(today.getFullYear(), today.getMonth(), 1);
    const lastDay = new Date(today.getFullYear(), today.getMonth() + 1, 0);
    
    const formatDate = (date) => {
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    };

    if (!dateFrom && !dateTo) {
      setDateFrom(formatDate(firstDay));
      setDateTo(formatDate(lastDay));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const loadTotalVentas = async () => {
      // Si no hay fechas, no hacer la petición
      if (!dateFrom || !dateTo) {
        setTotalVentas(0);
        setCantidadPedidos(0);
        setCantidadPedidosSinCosto(0);
        setTotalCostoProductos(0);
        setTotalCostoEnvio(0);
        setTotalGanancia(0);
        setPorEstado(POR_ESTADO_VACIO);
        setTopProductos([]);
        setError(null);
        return;
      }

      setLoading(true);
      setError(null);
      
      try {
        const params = new URLSearchParams({
          desde: dateFrom,
          hasta: dateTo
        });
        
        const data = await ApiService.get(`/pedidos/ventas/total?${params.toString()}`);
        
        // El API devuelve { message, data: { totalVentas, cantidadPedidos, fechaDesde, fechaHasta } }
        const ventasData = data?.data || data;
        setTotalVentas(Number(ventasData?.totalVentas || 0));
        setCantidadPedidos(Number(ventasData?.cantidadPedidos || 0));
        setCantidadPedidosSinCosto(Number(ventasData?.cantidadPedidosSinCosto || 0));
        setTotalCostoProductos(Number(ventasData?.totalCostoProductos || 0));
        setTotalCostoEnvio(Number(ventasData?.totalCostoEnvio || 0));
        setTotalGanancia(Number(ventasData?.totalGanancia || 0));
        setPorEstado({ ...POR_ESTADO_VACIO, ...(ventasData?.porEstado || {}) });
        setTopProductos(Array.isArray(ventasData?.topProductos) ? ventasData.topProductos : []);
      } catch (err) {
        console.error('Error al obtener total de ventas:', err);
        setError(err?.message || 'Error al obtener el total de ventas');
        setTotalVentas(0);
        setCantidadPedidos(0);
        setCantidadPedidosSinCosto(0);
        setTotalCostoProductos(0);
        setTotalCostoEnvio(0);
        setTotalGanancia(0);
        setPorEstado(POR_ESTADO_VACIO);
        setTopProductos([]);
      } finally {
        setLoading(false);
      }
    };

    loadTotalVentas();
  }, [dateFrom, dateTo]);

  useEffect(() => {
    const loadPreciosEnvio = async () => {
      setLoadingEnvios(true);
      setEnviosError(null);
      try {
        const data = await ApiService.get('/configuracion/envios');
        const precios = data?.data || data;
        if (precios?.precioMinorista != null) {
          setPrecioMinorista(String(precios.precioMinorista));
        }
        if (precios?.precioMayorista != null) {
          setPrecioMayorista(String(precios.precioMayorista));
        }
      } catch (err) {
        console.error('Error al obtener precios de envío:', err);
        setEnviosError(err?.message || 'Error al obtener los precios de envío');
      } finally {
        setLoadingEnvios(false);
      }
    };

    loadPreciosEnvio();
  }, []);

  const guardarPreciosEnvio = async (event) => {
    event.preventDefault();
    const minorista = Number(precioMinorista);
    const mayorista = Number(precioMayorista);
    if (!Number.isFinite(minorista) || minorista < 0 || !Number.isFinite(mayorista) || mayorista < 0) {
      setEnviosError('Los precios de envío deben ser números mayores o iguales a 0');
      setEnviosOk(null);
      return;
    }

    setSavingEnvios(true);
    setEnviosError(null);
    setEnviosOk(null);
    try {
      const data = await ApiService.put('/configuracion/envios', {
        precioMinorista: minorista,
        precioMayorista: mayorista,
      });
      const precios = data?.data || data;
      setPrecioMinorista(String(precios.precioMinorista));
      setPrecioMayorista(String(precios.precioMayorista));
      setEnviosOk('Precios de envío actualizados');
    } catch (err) {
      console.error('Error al guardar precios de envío:', err);
      setEnviosError(err?.payload?.message || err?.message || 'Error al guardar los precios de envío');
    } finally {
      setSavingEnvios(false);
    }
  };

  const comisionHigher = totalVentas * PRECIO_CONFIG.COMISION_HIGHER;

  return (
    <div className="admin-page">
      <Row className="g-3">
        <Col xs={12} className="mb-2">
          <div className="d-flex align-items-center gap-2 flex-wrap">
            <small className="text-muted">Filtrar por fecha:</small>
            <Form.Control
              type="date"
              size="sm"
              value={dateFrom}
              onChange={(e) => setDateFrom(e.target.value)}
              style={{ maxWidth: 170 }}
              aria-label="Desde"
            />
            <span className="text-muted">—</span>
            <Form.Control
              type="date"
              size="sm"
              value={dateTo}
              onChange={(e) => setDateTo(e.target.value)}
              style={{ maxWidth: 170 }}
              aria-label="Hasta"
            />
            {(dateFrom || dateTo) && (
              <Button
                variant="link"
                size="sm"
                className="text-decoration-none p-1"
                onClick={() => { setDateFrom(''); setDateTo(''); }}
                aria-label="Limpiar filtros"
                style={{ color: '#000' }}
              >
                <FaTrash />
              </Button>
            )}
          </div>
          {error && (
            <Alert variant="danger" className="mt-2 mb-0" style={{ fontSize: '0.875rem' }}>
              {error}
            </Alert>
          )}
        </Col>
        <Col xs={12} md={6} xl={3}>
          <Card className="shadow-sm h-100 admin-card admin-card-sales">
            <Card.Body>
              <Card.Title className="mb-1">Total de ventas</Card.Title>
              {loading ? (
                <Spinner animation="border" size="sm" />
              ) : (
                <>
                  <div style={{ fontSize: '1.6rem', fontWeight: 700 }}>
                    {formatMoney(totalVentas)}
                  </div>
                  {cantidadPedidos > 0 && (
                    <small className="text-muted">
                      {cantidadPedidos} {cantidadPedidos === 1 ? 'pedido' : 'pedidos'}
                    </small>
                  )}
                </>
              )}
            </Card.Body>
          </Card>
        </Col>
        <Col xs={12} md={6} xl={3}>
          <Card className="shadow-sm h-100 admin-card admin-card-cost">
            <Card.Body>
              <Card.Title className="mb-1">Costo de productos</Card.Title>
              {loading ? (
                <Spinner animation="border" size="sm" />
              ) : (
                <div style={{ fontSize: '1.6rem', fontWeight: 700 }}>
                  {formatMoney(totalCostoProductos)}
                </div>
              )}
            </Card.Body>
          </Card>
        </Col>
        <Col xs={12} md={6} xl={3}>
          <Card className="shadow-sm h-100 admin-card admin-card-shipping-cost">
            <Card.Body>
              <Card.Title className="mb-1">Costo de envío</Card.Title>
              {loading ? (
                <Spinner animation="border" size="sm" />
              ) : (
                <div style={{ fontSize: '1.6rem', fontWeight: 700 }}>
                  {formatMoney(totalCostoEnvio)}
                </div>
              )}
            </Card.Body>
          </Card>
        </Col>
        <Col xs={12} md={6} xl={3}>
          <Card className="shadow-sm h-100 admin-card admin-card-profit">
            <Card.Body>
              <Card.Title className="mb-1">Ganancia</Card.Title>
              {loading ? (
                <Spinner animation="border" size="sm" />
              ) : (
                <>
                  <div style={{ fontSize: '1.6rem', fontWeight: 700 }}>
                    {formatMoney(totalGanancia)}
                  </div>
                  {cantidadPedidosSinCosto > 0 && (
                    <small className="text-muted">
                      No incluye {cantidadPedidosSinCosto}{' '}
                      {cantidadPedidosSinCosto === 1 ? 'pedido' : 'pedidos'} sin costo de producto.
                    </small>
                  )}
                </>
              )}
            </Card.Body>
          </Card>
        </Col>
        <Col xs={12} md={6}>
          <Card className="shadow-sm h-100 admin-card admin-card-commission">
            <Card.Body>
              <Card.Title className="mb-1">
                Comisión Higher ({Math.round(PRECIO_CONFIG.COMISION_HIGHER * 100)}%)
              </Card.Title>
              {loading ? (
                <Spinner animation="border" size="sm" />
              ) : (
                <div style={{ fontSize: '1.6rem', fontWeight: 700 }}>
                  {formatMoney(comisionHigher)}
                </div>
              )}
            </Card.Body>
          </Card>
        </Col>
        <Col xs={12} md={6}>
          <Card className="shadow-sm h-100 admin-card admin-card-status">
            <Card.Body>
              <Card.Title className="mb-3">Pedidos por estado</Card.Title>
              {loading ? (
                <Spinner animation="border" size="sm" />
              ) : (
                <div className="d-flex flex-wrap gap-3">
                  {ESTADOS_DASHBOARD.map((estado) => (
                    <div key={estado.id} className="d-flex flex-column align-items-start">
                      <Badge bg={estado.variant} className="admin-status-badge mb-1">
                        {estado.label}
                      </Badge>
                      <strong style={{ fontSize: '1.25rem' }}>{Number(porEstado[estado.id] || 0)}</strong>
                    </div>
                  ))}
                </div>
              )}
            </Card.Body>
          </Card>
        </Col>
        <Col xs={12}>
          <Card className="shadow-sm admin-card admin-card-top">
            <Card.Body>
              <Card.Title className="mb-3">Productos más vendidos</Card.Title>
              {loading ? (
                <Spinner animation="border" size="sm" />
              ) : topProductos.length === 0 ? (
                <p className="text-muted mb-0">No hay ventas en el período seleccionado.</p>
              ) : (
                <ol className="admin-top-productos mb-0">
                  {topProductos.map((producto, index) => (
                    <li key={producto.id || `${producto.nombre}-${index}`} className="d-flex justify-content-between gap-3 py-1">
                      <span>{producto.nombre || producto.id || 'Producto'}</span>
                      <strong>{Number(producto.cantidad || 0).toLocaleString()} u.</strong>
                    </li>
                  ))}
                </ol>
              )}
            </Card.Body>
          </Card>
        </Col>
        <Col xs={12}>
          <Card className="shadow-sm admin-card admin-card-shipping">
            <Card.Body>
              <Card.Title className="mb-2">Precios de envío</Card.Title>
              <p className="text-muted small mb-3">
                Se aplican automáticamente en el checkout según el tipo de compra. El retiro por Haedo no tiene costo.
              </p>
              {loadingEnvios ? (
                <Spinner animation="border" size="sm" />
              ) : (
                <Form onSubmit={guardarPreciosEnvio}>
                  <Row className="g-3 align-items-end">
                    <Col xs={12} sm={4}>
                      <Form.Label className="small mb-1">Minorista</Form.Label>
                      <Form.Control
                        type="number"
                        min="0"
                        step="1"
                        value={precioMinorista}
                        onChange={(e) => setPrecioMinorista(e.target.value)}
                        aria-label="Precio de envío minorista"
                      />
                    </Col>
                    <Col xs={12} sm={4}>
                      <Form.Label className="small mb-1">Mayorista</Form.Label>
                      <Form.Control
                        type="number"
                        min="0"
                        step="1"
                        value={precioMayorista}
                        onChange={(e) => setPrecioMayorista(e.target.value)}
                        aria-label="Precio de envío mayorista"
                      />
                    </Col>
                    <Col xs={12} sm={4}>
                      <Button
                        type="submit"
                        variant="dark"
                        disabled={savingEnvios}
                      >
                        {savingEnvios ? 'Guardando...' : 'Guardar'}
                      </Button>
                    </Col>
                  </Row>
                  {enviosError && (
                    <Alert variant="danger" className="mt-3 mb-0" style={{ fontSize: '0.875rem' }}>
                      {enviosError}
                    </Alert>
                  )}
                  {enviosOk && (
                    <Alert variant="success" className="mt-3 mb-0" style={{ fontSize: '0.875rem' }}>
                      {enviosOk}
                    </Alert>
                  )}
                </Form>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </div>
  );
}

export default Admin;


